import { FileText, Globe, MessageCircle, UserIcon } from 'lucide-react';
// eslint-disable-next-line no-use-before-define
import React, { FC } from 'react';

import HighlightedMentions, {
  highlightSearchText
} from '../../../javascript/components/HighlightedMentions';
import {
  getCommentSearchSnippet,
  getSearchTextSnippet,
  getUrlSearchSnippet
} from './matchContext';

import styles from './searchMatches.module.css';

const highlightUrlSearchText = (url: string, query: string) => {
  const snippet = getUrlSearchSnippet(url, query);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const matchStart = snippet.toLocaleLowerCase().indexOf(normalizedQuery);

  if (!normalizedQuery || matchStart === -1) return snippet;

  const matchEnd = matchStart + normalizedQuery.length;
  return (
    <>
      {snippet.slice(0, matchStart)}
      <strong className={styles.urlMatch}>
        {snippet.slice(matchStart, matchEnd)}
      </strong>
      {snippet.slice(matchEnd)}
    </>
  );
};

export type SearchMatchesProps = {
  assignees?: { id: number; name: string }[];
  comments?: string[];
  description?: string;
  includeGuests: boolean;
  query: string;
  url?: string;
  urlQuery?: string;
};

const SearchMatches: FC<SearchMatchesProps> = ({
  assignees = [],
  comments = [],
  description,
  includeGuests,
  query,
  url,
  urlQuery = query
}) => {
  if (!assignees.length && !comments.length && !description && !url) {
    return null;
  }

  return (
    <div className={styles.matches}>
      {assignees.map(assignee => (
        <div className={styles.match} key={assignee.id}>
          <UserIcon aria-hidden className={styles.icon} />
          <span className={styles.text}>
            Assignee: {highlightSearchText(assignee.name, query)}
          </span>
        </div>
      ))}
      {description && (
        <div className={styles.match}>
          <FileText aria-hidden className={styles.icon} />
          <HighlightedMentions
            className={styles.text}
            description={getSearchTextSnippet(description, query)}
            includeGuests={includeGuests}
            searchHighlightText={query}
          />
        </div>
      )}
      {url && (
        <div className={styles.match}>
          <Globe aria-hidden className={styles.icon} />
          <span className={styles.text} title={url}>
            {highlightUrlSearchText(url, urlQuery)}
          </span>
        </div>
      )}
      {comments.map((comment, index) => (
        <div className={styles.match} key={`${comment}-${index}`}>
          <MessageCircle aria-hidden className={styles.icon} />
          <HighlightedMentions
            className={styles.text}
            description={getCommentSearchSnippet(comment, query)}
            includeGuests={includeGuests}
            isComment
            searchHighlightText={query}
          />
        </div>
      ))}
    </div>
  );
};

export default SearchMatches;
